import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRegistrationInvitationAccess, redeemRegistrationInvitationAccess } from "../../lib/leaderboardApi";
import { RegistrationNotice } from "./RegistrationLayout";

export function useRegistrationInvitation(matchId, family, signedIn, required, { consumeLink = true } = {}) {
  const location = useLocation(), navigate = useNavigate();
  const token = consumeLink ? new URLSearchParams(location.search).get("invite") || "" : "";
  const [access, setAccess] = useState({ loading: Boolean(required), authorized: !required, error: "" });
  const [busy, setBusy] = useState(false);
  const loaded = useRef(0);
  const clearToken = useCallback(() => {
    if (!token) return;
    const search = new URLSearchParams(location.search); search.delete("invite");
    navigate({ pathname: location.pathname, search: search.toString() ? `?${search}` : "" }, { replace: true, state: location.state });
  }, [token, location.search, location.pathname, location.state, navigate]);
  const redeem = useCallback(async body => {
    setBusy(true); setAccess(current => ({ ...current, error: "" }));
    try {
      const result = await redeemRegistrationInvitationAccess(matchId, family, body);
      if (!result?.authorized) throw new Error("Invitation access could not be confirmed.");
      setAccess({ ...result, loading: false, error: "" });
      clearToken();
      return true;
    } catch (error) { setAccess(current => ({ ...current, loading: false, error: error.message || "This invitation could not be accepted." })); return false; }
    finally { setBusy(false); }
  }, [matchId, family, clearToken]);
  useEffect(() => {
    const version = ++loaded.current;
    if (!required) { setAccess({ loading: false, authorized: true, error: "" }); return; }
    if (!signedIn) { setAccess({ loading: false, authorized: false, error: "" }); return; }
    setAccess({ loading: true, authorized: false, error: "" });
    (async () => {
      try {
        const result = await getRegistrationInvitationAccess(matchId, family);
        if (version !== loaded.current) return;
        if (result?.authorized) { setAccess({ ...result, loading: false, error: "" }); clearToken(); }
        else if (token) await redeem({ token });
        else setAccess({ loading: false, authorized: false, error: "" });
      } catch { if (version === loaded.current) setAccess({ loading: false, authorized: false, error: "Invitation access could not load. Try your invitation code again." }); }
    })();
    return () => { loaded.current += 1; };
  }, [required, signedIn, matchId, family, token, redeem, clearToken]);
  return { ...access, busy, redeem, required };
}

export default function RegistrationInvitation({ invitation, signedIn }) {
  const [code, setCode] = useState("");
  if (!invitation.required) return null;
  return <RegistrationNotice title={invitation.authorized ? "Invitation accepted" : "An invitation is required"}>
    {invitation.authorized ? <p>Your invitation is linked to this account. Registration deadlines and available places still apply.</p>
      : !signedIn ? <p>Sign in with your account to use your invitation link or enter an access code from the director.</p>
      : <><p>{invitation.loading || invitation.busy ? "Checking your invitation…" : "Enter the access code the director shared with you."}</p>
        <div className="entry-invitation"><label className="entry-field"><span>Invitation code</span><input autoComplete="off" spellCheck="false" value={code} disabled={invitation.busy} onChange={event => setCode(event.target.value)} /></label>
          <button type="button" className="entry-secondary" disabled={!code.trim() || invitation.busy} onClick={async () => { if (await invitation.redeem({ code: code.trim() })) setCode(""); }}>Apply code</button></div></>}
    {invitation.error && <p role="alert">{invitation.error}</p>}
  </RegistrationNotice>;
}
